import { Translate } from 'next-translate';
import { contentType } from '#config';
import { ContentType, MediaType } from '#types/generated/graphql-types';

export enum ENTITY_ROUTE {
  LIVESTREAMS = 'UrlLivestreams',
  MOVIES = 'UrlMovies',
  PODCASTS = 'UrlPodcasts',
  SERIES = 'UrlSeries',
}

export const ROUTE_TRANSLATION_MAPPING = {
  series: ENTITY_ROUTE.SERIES,
  podcasts: ENTITY_ROUTE.PODCASTS,
  movies: ENTITY_ROUTE.MOVIES,
  livestreams: ENTITY_ROUTE.LIVESTREAMS,
};

interface GqlDataWithIri {
  iri: string;
  slug: string;
  contentType?: ContentType;
  mediaType?: MediaType;
  seriesSlug?: string;
}

interface GqlDataWithoutIri {
  iri?: string;
  slug: string;
  contentType: ContentType;
  mediaType?: MediaType;
  seriesSlug?: string;
}

// You can only pass a parameter that meets the requirements of `OnlyOne`
// OR
// you can only pass a parameter that meets the requirements of `OnlyTheOther`
type ItemProps = GqlDataWithIri | GqlDataWithoutIri;

function getContentTypeFromIri(iri: string): ContentType {
  if (iri.includes('/audios/')) {
    return ContentType.Audio;
  }
  if (iri.includes('/videos/')) {
    return ContentType.Video;
  }
  if (iri.includes('/live_streams/')) {
    return ContentType.LiveStream;
  }
  if (iri.includes('/series/')) {
    return ContentType.Series;
  }
  if (iri.includes('/season/')) {
    return ContentType.Season;
  }
  if (iri.includes('/studio/')) {
    return ContentType.Studio;
  }
}

const getRoute = (
  item: ItemProps,
  t: Translate,
  language?: string
): string | null => {
  // First path component always is the language

  if (!item) return null;

  const pathComponents = [];

  if (language) {
    pathComponents.push(language);
  }

  const itemContentType = item.contentType || getContentTypeFromIri(item.iri);
  const itemMediaType = item.mediaType;

  // Find subpath for type. Could be empty
  if (
    itemContentType === ContentType.Audio ||
    itemContentType === ContentType.LiveStream ||
    itemContentType === ContentType.Video
  ) {
    if (itemMediaType === MediaType.Episode) {
      if (item.seriesSlug) {
        pathComponents.push(`${t(ENTITY_ROUTE.SERIES)}/${item.seriesSlug}`);
      } else if (item?.['season']?.series?.slug) {
        /** TODO check if still valid and used, cleanup and remove ['season'] */
        pathComponents.push(
          `${t(ENTITY_ROUTE.SERIES)}/${item?.['season']?.series?.slug}`
        );
      }
    } else {
      let route = ENTITY_ROUTE.MOVIES;

      if (itemContentType === ContentType.Audio) {
        route = ENTITY_ROUTE.PODCASTS;
      }

      if (itemContentType === ContentType.LiveStream) {
        route = ENTITY_ROUTE.LIVESTREAMS;
      }

      pathComponents.push(t(route));
    }
    /** TODO clean this up and why is series a type? */
  } else if (itemContentType === ContentType.Series) {
    pathComponents.push(t(ENTITY_ROUTE.SERIES));
  } else if (itemContentType === ContentType.Studio) {
    pathComponents.push(t(contentType[ContentType.Studio].frontendRoute));
  } else if (itemContentType === ContentType.Category) {
    pathComponents.push(t(contentType[ContentType.Category].frontendRoute));
  }

  // Last path component always is the item's slug
  if (item.slug) {
    pathComponents.push(item.slug);
  }

  return `/${pathComponents.join('/')}`.toLocaleLowerCase();
};

export default getRoute;
